import Immutable from 'immutable';
import type { TextDirection } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { createSelector } from '@reduxjs/toolkit';

import type { LanguageLocalesEnum, SlideTranslatedText, SupportedLanguages } from 'js/src/client';
import { LanguageEnum } from 'js/src/client';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import StoreORM from 'js/src/data/store-orm';
import { getOriginalTranscriptLanguage, TypeOfTranscript } from 'js/src/data/transcript';
import { getTranslatedLangCodeFromSceneGroupId } from 'js/src/data/translated-editors';
import type { TranslationSelectValue } from 'js/src/data/translations/translations-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { DROPDOWN_ACTIONS } from 'js/src/libs/video-translation-consts';
import { stringifyUnknown } from 'js/src/libs/custom-utils';

export const getVideoTranslationBySceneId = (sceneId: string) => (state: RootState) => {
    const orm = new StoreORM<SlideTranslatedText, string>(state.translations.translations);
    const text = orm.getByIdOrUndefined(sceneId) && orm.getById(sceneId).get('message');
    return text;
};

export const doesSceneHaveTranslations = (sceneId: string) => (state: RootState) => {
    const text = getVideoTranslationBySceneId(sceneId)(state);
    return !!text;
};

export const hasLoadedSupportedLanguages = (state: RootState) => {
    return state.translations.supported_languages.size > 0;
};

export const isLoadingSupportedLanguages = (state: RootState) => {
    return state.translations.is_loading_supported_languages;
};

export const getLanguageName = (state: RootState, langCode: string | undefined | null) => {
    if (!langCode) {
        return undefined;
    }
    if (!hasLoadedSupportedLanguages(state)) {
        return undefined;
    }

    const supported_languages = getSupportedLanguages(state);
    const langItem = supported_languages.find((langItem) => {
        return langItem.get('code') === langCode;
    });
    return langItem && langItem.get('name');
};

export function getSupportedLanguages(state: RootState) {
    return state.translations.supported_languages;
}

export const getSpeechRecognitionLanguagesSelectOptions = createSelector(
    getSupportedLanguages,
    (supportedLanguages) => {
        return supportedLanguages
            .filter((l) => {
                return l.get('show_as_speech_recognition_language');
            })
            .map((l) => {
                return {
                    label: l.get('name'),
                    value: l.get('code'),
                };
            })
            .toJS();
    },
);

/**
 * This selector returns a list of dropdown item props for the translation dropdown.
 */
export const selectTranslationDropdownItemProps = createSelector(
    (state: RootState) => state.translations.supported_languages,
    (
        supported_languages,
    ): Immutable.List<
        DropdownItemProp<{
            type: DROPDOWN_ACTIONS;
            language: string;
            language_code: string;
        }>
    > => {
        if (supported_languages.size === 0) {
            return Immutable.List();
        }

        // filter to only languages supported for translation!
        const supported_languages_for_translation = supported_languages.filter((langItem) => {
            return langItem.get('supports_translation');
        });

        // create one option per language
        const translationOptions = supported_languages_for_translation.map(
            (langItem) =>
                ImmutableFromJS({
                    id: langItem.get('code'),
                    text: langItem.get('name'),
                    onSelectData: {
                        type: DROPDOWN_ACTIONS.TRANSLATE,
                        language_code: langItem.get('code'),
                        language: langItem.get('name'),
                    },
                }) as DropdownItemProp<{
                    type: DROPDOWN_ACTIONS;
                    language: string;
                    language_code: string;
                }>,
        );

        const sortedTranslationOptions = translationOptions.sort((a, b) => {
            const aText = a.get('text');
            const bText = b.get('text');
            if (!aText || !bText) {
                return 0;
            }
            if (aText < bText) {
                return -1;
            }
            if (aText > bText) {
                return 1;
            }
            return 0;
        });

        return sortedTranslationOptions.toList();
    },
);

export const getLanguageItem = (state: RootState, langCode: string | null) => {
    if (!langCode) {
        return undefined;
    }
    if (!hasLoadedSupportedLanguages(state)) {
        return undefined;
    }
    const supportedLanguages = getSupportedLanguages(state);
    const langItem = supportedLanguages.find((langItem) => langItem.get('code') === langCode);
    return langItem;
};

export const getLanguageReadingSpeed = (state: RootState, langCode: string | null) => {
    const langItem = getLanguageItem(state, langCode);
    return langItem && langItem.get('reading_speed');
};

export const getLanguageTextDirection = (state: RootState, langCode: string) => {
    const langItem = getLanguageItem(state, langCode);
    if (langItem) {
        return langItem.get('text_direction') as TextDirection;
    }
    return VideoData.LEFT_TO_RIGHT_TEXT_DIRECTION;
};

export const shouldSeparateWordsWithSpaces = (
    state: RootState,
    transcriptLanguage: string,
): boolean => {
    const supportedLanguages = getSupportedLanguages(state);
    const languageInfo = supportedLanguages.find(
        (language) => language.get('code') === transcriptLanguage,
    );
    if (languageInfo) {
        return languageInfo.get('words_separated_by_spaces');
    } else {
        return true;
    }
};

export const shouldSeparateTranscriptWordsWithSpaces = (
    state: RootState,
    sceneGroupId: string,
    typeOfTranscript: TypeOfTranscript,
) => {
    const langCode =
        typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
            ? getOriginalTranscriptLanguage(state, sceneGroupId)
            : getTranslatedLangCodeFromSceneGroupId(state, sceneGroupId);
    return shouldSeparateWordsWithSpaces(state, langCode || LanguageEnum.XX);
};

export const getTranslationSelectValues = createSelector(
    (state: RootState, onlyIncludeAIVoiceoverSupportedLanguages: boolean) =>
        getSupportedLanguages(state),
    (state: RootState, onlyIncludeAIVoiceoverSupportedLanguages: boolean) =>
        onlyIncludeAIVoiceoverSupportedLanguages,
    (
        supportedLanguages: Immutable.List<ImmutableMap<SupportedLanguages>>,
        onlyIncludeAIVoiceoverSupportedLanguages,
    ) => {
        const a: TranslationSelectValue[] = [];
        const translationLanguages = supportedLanguages.filter((l) => {
            const supportsTranslation = l.get('supports_translation');
            const supportsAIVoiceover = l.get('supports_ai_voiceover');
            if (onlyIncludeAIVoiceoverSupportedLanguages) {
                return supportsTranslation && supportsAIVoiceover;
            }
            return supportsTranslation;
        });
        translationLanguages.forEach((lang) => {
            const locales = lang.get('translation_locales');
            return locales && locales.size > 0
                ? locales.forEach((locale) =>
                      a.push({
                          label: locale.get('name'),
                          value: locale.get('code'),
                      }),
                  )
                : a.push({
                      label: lang.get('name'),
                      value: lang.get('code') as unknown as LanguageLocalesEnum,
                  });
        });
        return a.sort((a: TranslationSelectValue, b: TranslationSelectValue) => {
            const aLabel = stringifyUnknown(a.label);
            const bLabel = stringifyUnknown(b.label);
            if (aLabel < bLabel) {
                return -1;
            }
            if (aLabel > bLabel) {
                return 1;
            }
            return 0;
        });
    },
);
